<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('unicodeConverter.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('unicodeConverter.title') }}</h1>
      <p>{{ $t('unicodeConverter.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="mode-tabs">
        <button v-for="m in modes" :key="m.key" :class="['tab-btn', { active: mode === m.key }]" @click="mode = m.key; convert()">
          {{ m.label }}
        </button>
      </div>

      <div class="codec-layout">
        <div class="codec-panel">
          <label>{{ $t('unicodeConverter.inputLabel') }}</label>
          <textarea v-model="input" :placeholder="$t('unicodeConverter.inputPlaceholder')" rows="8" spellcheck="false"></textarea>
        </div>

        <div class="codec-actions">
          <button class="btn btn-primary" @click="convert">{{ $t('common.convert') }} →</button>
          <button class="btn btn-secondary" @click="reverseConvert">← {{ $t('unicodeConverter.reverse') }}</button>
        </div>

        <div class="codec-panel">
          <label>{{ $t('unicodeConverter.outputLabel') }}</label>
          <textarea v-model="output" rows="8" spellcheck="false" readonly></textarea>
        </div>
      </div>

      <div class="action-bar">
        <span class="status-msg" v-if="statusMsg">{{ statusMsg }}</span>
        <button class="btn btn-outline" @click="copyResult">{{ $t('common.copy') }}</button>
        <button class="btn btn-outline" @click="clear">{{ $t('common.clear') }}</button>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('unicodeConverter.whatIs') }}</h2>
      <p>{{ $t('unicodeConverter.whatIsDesc') }}</p>
      <h3>{{ $t('unicodeConverter.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('unicodeConverter.useCases')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UnicodeConverter',
  data() {
    return {
      input: '',
      output: '',
      mode: 'unicode',
      statusMsg: ''
    }
  },
  computed: {
    modes() {
      return [
        { key: 'unicode', label: 'Unicode (\\uXXXX)' },
        { key: 'hex', label: 'Hex (\\xXX)' },
        { key: 'decimal', label: 'HTML Entity (&#NNN;)' },
        { key: 'utf8', label: 'UTF-8 Hex' }
      ]
    }
  },
  methods: {
    convert() {
      if (!this.input.trim()) return
      try {
        switch (this.mode) {
          case 'unicode':
            this.output = this.toUnicode(this.input); break
          case 'hex':
            this.output = this.toHex(this.input); break
          case 'decimal':
            this.output = this.toHtmlEntity(this.input); break
          case 'utf8':
            this.output = this.toUtf8Hex(this.input); break
        }
        this.statusMsg = this.$t('common.success')
      } catch (e) {
        this.statusMsg = this.$t('common.error') + ': ' + e.message
      }
    },
    reverseConvert() {
      if (!this.input.trim()) return
      try {
        switch (this.mode) {
          case 'unicode':
            this.output = this.fromUnicode(this.input); break
          case 'hex':
            this.output = this.fromHex(this.input); break
          case 'decimal':
            this.output = this.fromHtmlEntity(this.input); break
          case 'utf8':
            this.output = this.fromUtf8Hex(this.input); break
        }
        this.statusMsg = this.$t('common.success')
      } catch (e) {
        this.statusMsg = this.$t('common.error') + ': ' + e.message
      }
    },
    toUnicode(str) {
      return str.split('').map(c => {
        const code = c.charCodeAt(0)
        return code > 127 ? '\\u' + code.toString(16).padStart(4, '0') : c
      }).join('')
    },
    fromUnicode(str) {
      return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    },
    toHex(str) {
      return str.split('').map(c => '\\x' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    },
    fromHex(str) {
      return str.replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    },
    toHtmlEntity(str) {
      return str.split('').map(c => '&#' + c.charCodeAt(0) + ';').join('')
    },
    fromHtmlEntity(str) {
      return str.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec)))
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    },
    toUtf8Hex(str) {
      const encoder = new TextEncoder()
      const bytes = encoder.encode(str)
      return Array.from(bytes).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ')
    },
    fromUtf8Hex(str) {
      const bytes = str.trim().split(/\s+/).map(h => parseInt(h, 16))
      const decoder = new TextDecoder()
      return decoder.decode(new Uint8Array(bytes))
    },
    clear() { this.input = ''; this.output = ''; this.statusMsg = '' },
    async copyResult() {
      if (!this.output) return
      try {
        await navigator.clipboard.writeText(this.output)
        this.statusMsg = this.$t('common.copied')
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1000px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }
.mode-tabs { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
.tab-btn { padding: 8px 16px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-secondary); cursor: pointer; font-size: 13px; transition: all 0.2s; }
.tab-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }
.codec-layout { display: flex; gap: 16px; align-items: stretch; }
.codec-panel { flex: 1; display: flex; flex-direction: column; }
.codec-panel label { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.codec-panel textarea { flex: 1; min-height: 180px; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 14px; resize: vertical; outline: none; box-sizing: border-box; }
.codec-panel textarea:focus { border-color: var(--accent-color); }
.codec-actions { display: flex; flex-direction: column; justify-content: center; gap: 10px; }
.btn { padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; white-space: nowrap; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-secondary:hover { border-color: var(--accent-color); }
.btn-outline { background: transparent; color: var(--text-primary); border: 1px solid var(--border-color); padding: 8px 16px; }
.btn-outline:hover { border-color: var(--accent-color); color: var(--accent-color); }
.action-bar { display: flex; align-items: center; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); flex: 1; }
@media (max-width: 768px) {
  .codec-layout { flex-direction: column; }
  .codec-actions { flex-direction: row; justify-content: center; }
}
</style>
